import React from 'react';
import { Drawer, Box, TextField, Stack, Paper, MenuItem, FormLabel } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff, faTrash } from '@fortawesome/free-solid-svg-icons';
import Icon from '../../../components/Elements/Icon';

import featuresService from '../../../services/features';

function FeatureDrawer({ open, onClose, featureData, setFeatureData, onSave, isNew }) {
  const [newRule, setNewRule] = React.useState({
    rule_ref_field: 'email',
    rule_type: 'equals',
    rule_value: '',
    is_enabled: 1,
  });

  const handleRuleTypeChange = (e) => {
    const rule_type = e.target.value;
    setNewRule({ rule_ref_field: 'email', rule_type, rule_value: '', is_enabled: 1 });
  };

  if (!featureData) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFeatureData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleDefault = (newState) => {
    setFeatureData((prev) => ({ ...prev, default_state: newState }));
  };
  const handleToggleRuleEnabled = async (ruleIdx) => {
    const updatedRules = [...featureData.rules];
    const rule = updatedRules[ruleIdx];
    const newEnabledState = rule.is_enabled ? 0 : 1;

    const saved = await featuresService.saveFeatureRule(featureData.name, {
      ...rule,
      is_enabled: newEnabledState,
    });

    if (saved?.rule_id) {
      updatedRules[ruleIdx].is_enabled = newEnabledState;
      setFeatureData((prev) => ({ ...prev, rules: updatedRules }));
    }
  };

  const handleAddRule = async () => {
    if (newRule.rule_type === 'percentage') {
      const pct = Number(newRule.rule_value);
      if (isNaN(pct) || pct < 0 || pct > 100) return;
    } else {
      if (!newRule.rule_value?.trim()) return;
    }

    const saved = await featuresService.saveFeatureRule(featureData.name, newRule);

    if (saved?.rule_id) {
      const updatedRules = [
        ...(featureData.rules || []).filter(
          (r) => !(newRule.rule_type === 'percentage' && r.rule_type === 'percentage')
        ),
        { ...newRule, id: saved.rule_id, exceptions: [] },
      ];
      setFeatureData((prev) => ({ ...prev, rules: updatedRules }));
      setNewRule({ rule_ref_field: 'email', rule_type: 'equals', rule_value: '', is_enabled: 1 });
    }
  };

  const handleDeleteRule = async (ruleIdx) => {
    const rule = featureData.rules[ruleIdx];
    if (!rule?.id) return;
    const confirmed = window.confirm('Delete this rule?');
    if (!confirmed) return;
    const success = await featuresService.deleteFeatureRule(rule.id);
    if (success) {
      const updatedRules = featureData.rules.filter((_, i) => i !== ruleIdx);
      setFeatureData((prev) => ({ ...prev, rules: updatedRules }));
    }
  };

  const handleSaveException = async (ruleIdx, exIdx) => {
    const rule = featureData.rules[ruleIdx];
    const exception = rule.exceptions[exIdx];
    if (!exception.ref_field_value?.trim()) return;
    const saved = await featuresService.saveFeatureException(featureData.id, {
      rule_id: rule.id,
      ref_field: exception.ref_field,
      ref_field_value: exception.ref_field_value,
    });
    if (saved?.id) {
      const updatedRules = [...featureData.rules];
      updatedRules[ruleIdx].exceptions[exIdx] = { ...saved };
      setFeatureData((prev) => ({ ...prev, rules: updatedRules }));
    }
  };

  const handleDeleteException = async (ruleIdx, exIdx) => {
    const exception = featureData.rules[ruleIdx].exceptions[exIdx];
    if (!exception?.id) return;
    const confirmed = window.confirm('Delete this exception?');
    if (!confirmed) return;
    const success = await featuresService.deleteFeatureException(exception.id);
    if (success) {
      const updatedRules = [...featureData.rules];
      updatedRules[ruleIdx].exceptions = updatedRules[ruleIdx].exceptions.filter(
        (_, i) => i !== exIdx
      );
      setFeatureData((prev) => ({ ...prev, rules: updatedRules }));
    }
  };
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 600, padding: 3 }}>
        <div className="fullWidthContainer large bold Padded">
          {isNew ? 'Create New Feature' : 'Edit Feature'}
        </div>

        {/* Feature Basic Info */}
        <div className="fullWidthContainer Padded">
          <TextField
            fullWidth
            label="Feature Name"
            name="name"
            value={featureData.name || ''}
            onChange={handleChange}
            disabled={!isNew}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={featureData.description || ''}
            onChange={handleChange}
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <div className="linkText large">
            {featureData.default_state ? (
              <FontAwesomeIcon
                icon={faToggleOn}
                className="purpleText"
                onClick={() => handleToggleDefault(0)}
              />
            ) : (
              <FontAwesomeIcon
                icon={faToggleOff}
                className="dkGrayText"
                onClick={() => handleToggleDefault(1)}
              />
            )}
          </div>
          <button className="buttonClone" onClick={onSave}>
            Save
          </button>
        </div>

        {/* Rules Section */}
        <Box className="fullWidthContainer Padded">
          <div className="bold large">Rules</div>
          <div className="fullWidthContainer Padded">
            Rules are applied in the order they are listed and override the default state of a
            feature.
          </div>
          {/* Existing Rules */}
          {(featureData.rules || []).length === 0 ? (
            <div className="Padded fullWidthContainer">
              <hr />
              No rules found.
            </div>
          ) : (
            <Stack spacing={2} className="fullWidthContainer Padded">
              {(featureData.rules || []).map((rule, ruleIdx) => (
                <Box
                  key={rule.id || ruleIdx}
                  className="Padded fullWidthContainer"
                  sx={{
                    backgroundColor: '#fff',
                    padding: 1,
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                  }}
                >
                  <div>
                    {rule.rule_type === 'percentage' ? (
                      <>
                        <strong style={{ marginRight: '6px' }}>{rule.rule_value}%</strong>
                        of new signups — feature is{' '}
                      </>
                    ) : (
                      <>
                        If{' '}
                        <strong style={{ marginLeft: '6px', marginRight: '6px' }}>
                          {rule.rule_ref_field}
                        </strong>{' '}
                        equals{' '}
                        <strong style={{ marginLeft: '6px', marginRight: '6px' }}>
                          {rule.rule_value}
                        </strong>
                        feature is{' '}
                      </>
                    )}
                    <FontAwesomeIcon
                      icon={rule.is_enabled ? faToggleOn : faToggleOff}
                      className={rule.is_enabled ? 'purpleText' : 'dkGrayText'}
                      onClick={() => handleToggleRuleEnabled(ruleIdx)}
                      style={{ marginLeft: '10px' }}
                    />
                  </div>

                  <div>
                    <FontAwesomeIcon
                      icon={faTrash}
                      className="dkGrayText"
                      onClick={() => handleDeleteRule(ruleIdx)}
                      style={{ marginLeft: '10px' }}
                    />
                  </div>
                </Box>
              ))}
            </Stack>
          )}

          {/* Add Rule */}
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: 3,
              border: '1px solid #ccc',
              borderRadius: '4px',
              marginTop: '20px',
              position: 'relative',
            }}
            className="Padded fullWidthContainer"
          >
            <FormLabel
              sx={{
                position: 'absolute',
                top: '-8px',
                left: '12px',
                backgroundColor: 'background.paper',
                padding: '0 4px',
                fontSize: '0.75rem',
                color: 'text.secondary',
              }}
            >
              Add New Rule
            </FormLabel>
            <TextField
              label="Rule Type"
              select
              value={newRule.rule_type}
              onChange={handleRuleTypeChange}
              size="small"
              sx={{ mr: 2 }}
            >
              <MenuItem value="equals">Equals</MenuItem>
              <MenuItem value="percentage">Percentage Rollout</MenuItem>
            </TextField>
            {newRule.rule_type === 'percentage' ? (
              <TextField
                label="% of new signups (0–100)"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                value={newRule.rule_value}
                onChange={(e) => setNewRule((prev) => ({ ...prev, rule_value: e.target.value }))}
                size="small"
                sx={{ mr: 2, width: 200 }}
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <>
                <TextField
                  label="Field"
                  select
                  value={newRule.rule_ref_field}
                  onChange={(e) =>
                    setNewRule((prev) => ({ ...prev, rule_ref_field: e.target.value }))
                  }
                  size="small"
                  sx={{ mr: 2 }}
                >
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="user_id">User ID</MenuItem>
                </TextField>
                Equals
                <TextField
                  label="Field Value"
                  value={newRule.rule_value}
                  onChange={(e) =>
                    setNewRule((prev) => ({ ...prev, rule_value: e.target.value }))
                  }
                  size="small"
                  sx={{ mr: 2 }}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
            Feature is
            <FontAwesomeIcon
              icon={newRule.is_enabled ? faToggleOn : faToggleOff}
              className={newRule.is_enabled ? 'purpleText' : 'dkGrayText'}
              onClick={() =>
                setNewRule((prev) => ({ ...prev, is_enabled: prev.is_enabled ? 0 : 1 }))
              }
              style={{ marginLeft: '5px', cursor: 'pointer' }}
            />
            <div className="fullWidthContainer Padded">
              <span className="TwoPortionFlex">&nbsp;</span>
              <button className="buttonClone small OnePortionFlex" onClick={handleAddRule}>
                Add Rule
              </button>
            </div>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

export default FeatureDrawer;
